import { useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { TopBar, DestinationRow, ActionButton, Field, FieldError, SecondaryAction } from "./kit";
import LogMatchGroundStep from "./LogMatchGroundStep";
import LogMatchAddGroundPanel from "./LogMatchAddGroundPanel";
import { useChromelessOverride } from "../context/ChromelessOverrideContext";
import { AccessibilityMenu } from "./AccessibilityMenu";
import { AccountMenu } from "./AccountMenu";
import { NotificationsBell } from "./NotificationsBell";
import api from "../lib/api";
import { planDateMax, planDateLabel } from "../lib/comingUp";
import { todayIso, clientDateHeaders } from "../lib/trainingStatus";
import { useFocusFirstInvalid } from "../lib/focusFirstInvalid";

const NO_PRESET = { groundId: null, name: null, latitude: null, longitude: null };

/**
 * Issue #1149 (D214.3, D225 item 3) — Plan a match, a page of its own with
 * Back to Plan. Drawn in `PlanMatch.dc.html` (round 10). A step of
 * `/development` (`?plan=match`, the way `?milestone=` is), mounted by
 * `pages/DevelopmentPlan.jsx`; there is no new route.
 *
 * Saving is `POST /matches` with `status: "planned"` (#1148): date, time
 * (`match_time`, D231), opposition, competition, ground and team, and no
 * figures. A planned match never counts as played, so the body carries none.
 * Date, opposition, ground and competition are required (the API requires
 * them; the board marks none, and says the build asks which). The board draws
 * no Competition field, but the API refuses a match without one, so this
 * screen asks for it rather than invent a value.
 *
 * D57: a failed save is said in place, in ink with the alert rule, and what
 * was typed stays; success is a toast and nothing else, then the caller takes
 * the player back to Plan, where the match is a row in Coming up. Ground
 * "opens the same ground step the log flow uses": `LogMatchGroundStep` (and its
 * add-a-ground panel) rendered in place of the form, which keeps its state.
 */
export default function PlanMatchScreen({ onBack, onSaved }) {
  const { setActive: setChromeless } = useChromelessOverride();
  useEffect(() => {
    setChromeless(true);
    return () => setChromeless(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [view, setView] = useState("form"); // "form" | "ground" | "add-ground"
  const [addSeed, setAddSeed] = useState("");
  const [form, setForm] = useState({ date: "", time: "", opposition: "", competition: "", team: "" });
  const [ground, setGround] = useState(null); // { name, id }
  const [errors, setErrors] = useState({});
  const [saveState, setSaveState] = useState("idle"); // "idle" | "saving" | "failed"
  // #1299 (D303): Save scrolls to the first missing field and focuses it.
  const rootRef = useRef(null);
  const armFocus = useFocusFirstInvalid(rootRef, errors);

  const set = (key) => (e) => {
    const value = e.target.value;
    setForm((f) => ({ ...f, [key]: value }));
    if (errors[key]) setErrors((x) => ({ ...x, [key]: undefined }));
  };

  const pickGround = (name, id) => {
    setGround({ name, id });
    setErrors((x) => ({ ...x, ground: undefined }));
    setView("form");
  };

  const save = async () => {
    armFocus();
    const next = {};
    if (!form.date) next.date = "Pick the day";
    else {
      // `min`/`max` on the input are hints a typed date walks past; this is
      // the check. A date outside the range would save and never show in
      // Coming up (#1240 F1b).
      const today = todayIso();
      const max = planDateMax(today);
      if (form.date < today || form.date > max) {
        next.date = `Pick a date between today and ${planDateLabel(max)}.`;
      }
    }
    if (!form.opposition.trim()) next.opposition = "Who are you playing?";
    if (!form.competition.trim()) next.competition = "Which competition is it?";
    if (!ground) next.ground = "Choose a ground";
    setErrors(next);
    if (Object.keys(next).length > 0) return;

    setSaveState("saving");
    const body = {
      status: "planned",
      match_date: form.date,
      opponent: form.opposition.trim(),
      competition: form.competition.trim(),
      ground_id: ground.id,
    };
    if (form.time) body.match_time = form.time;
    if (form.team.trim()) body.team = form.team.trim();
    try {
      // The API windows the date on the player's own day (#1245).
      await api.post("/matches", body, { headers: clientDateHeaders() });
    } catch (e) {
      console.error("PlanMatchScreen: failed to plan the match", e);
      setSaveState("failed");
      return;
    }
    toast.success("Match planned");
    onSaved?.();
  };

  const chrome = (
    <>
      <TopBar
        variant="white"
        homeHref="/dashboard"
        homeLabel="CricketCompass — go to your dashboard"
        endControls={
          <>
            <NotificationsBell compact surface="white" />
            <AccessibilityMenu compact surface="white" />
            <AccountMenu empty surface="white" hasRibbon />
          </>
        }
        data-testid="plan-match-topbar"
      />
      <DestinationRow current="plan" />
    </>
  );

  if (view === "ground" || view === "add-ground") {
    return (
      <div data-testid="plan-match-ground" style={{ color: "var(--nw-ink)" }}>
        {chrome}
        {view === "add-ground" ? (
          <LogMatchAddGroundPanel
            seed={addSeed}
            future
            onSaved={(g) => pickGround(g.name, g.ground_id)}
            onCancel={() => setView("ground")}
          />
        ) : (
          <LogMatchGroundStep
            presetGround={NO_PRESET}
            future
            onPicked={pickGround}
            onAddGround={(seed) => {
              setAddSeed(seed || "");
              setView("add-ground");
            }}
            onExitStep1={() => setView("form")}
          />
        )}
      </div>
    );
  }

  return (
    <div ref={rootRef} data-testid="plan-match" style={{ color: "var(--nw-ink)" }}>
      {chrome}
      <div className="flex flex-col" style={{ gap: 14, padding: "var(--nw-space-page)" }}>
        <SecondaryAction
          variant="link"
          data-testid="plan-match-back"
          onClick={onBack}
          style={{ display: "flex", width: "fit-content", alignItems: "center", gap: 4 }}
        >
          <ChevronLeft size={16} aria-hidden="true" />
          Back to Plan
        </SecondaryAction>

        <div className="flex flex-col" style={{ gap: 4 }}>
          <div className="nw-eyebrow">Plan</div>
          <div className="nw-title-lg">Plan a match</div>
          <div className="nw-lede">A match you're going to play. It shows in Coming up until the day.</div>
        </div>

        <Field
          label="Date"
          type="date"
          min={todayIso()}
          max={planDateMax(todayIso())}
          data-testid="pm-date"
          value={form.date}
          onChange={set("date")}
          error={errors.date}
        />
        <Field label="Time" type="time" data-testid="pm-time" value={form.time} onChange={set("time")} />
        <Field
          label="Opposition"
          placeholder="Who are you playing?"
          data-testid="pm-opposition"
          value={form.opposition}
          onChange={set("opposition")}
          error={errors.opposition}
        />
        <Field
          label="Competition"
          placeholder="Which competition is it?"
          data-testid="pm-competition"
          value={form.competition}
          onChange={set("competition")}
          error={errors.competition}
        />

        <div>
          <div className="nw-label" style={{ marginBottom: 4 }}>Ground</div>
          <button
            type="button"
            data-testid="pm-ground"
            data-field-invalid={errors.ground ? "true" : undefined}
            aria-describedby={errors.ground ? "pm-ground-error" : undefined}
            onClick={() => setView("ground")}
            className="flex items-center justify-between"
            style={{
              width: "100%",
              height: "var(--nw-size-control)",
              padding: "0 12px",
              background: "var(--nw-surface)",
              border: errors.ground ? "var(--nw-border-invalid)" : "1px solid var(--nw-line)",
              borderRadius: "var(--nw-radius-control)",
              fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
              fontSize: 15,
              color: ground ? "var(--nw-ink)" : "var(--nw-ink-muted)",
              textAlign: "left",
              cursor: "pointer",
            }}
          >
            <span>{ground ? ground.name : "Choose a ground"}</span>
            <ChevronRight size={18} aria-hidden="true" style={{ color: "var(--nw-ink-faint)" }} />
          </button>
          {errors.ground && (
            <FieldError id="pm-ground-error" testId="pm-ground-error">
              {errors.ground}
            </FieldError>
          )}
        </div>

        <Field
          label="Your team"
          placeholder="Which of your teams?"
          data-testid="pm-team"
          value={form.team}
          onChange={set("team")}
        />

        {saveState === "failed" && (
          <div
            data-testid="plan-match-error"
            style={{
              fontSize: 13,
              lineHeight: 1.5,
              color: "var(--nw-ink)",
              padding: "10px 12px",
              background: "var(--nw-line-faint)",
              borderLeft: "3px solid var(--nw-alert)",
              borderRadius: "var(--nw-radius-control)",
            }}
          >
            That didn't save. Nothing is planned yet. Try again.
          </div>
        )}

        <ActionButton
          data-testid="plan-match-save"
          onClick={save}
          disabled={saveState === "saving"}
          icon={saveState === "saving" ? <Loader2 size={18} style={{ color: "#0F172A" }} className="animate-spin" /> : null}
          style={saveState === "saving" ? { color: "#0F172A", opacity: 1 } : { color: "#0F172A" }}
        >
          {saveState === "saving" ? "Saving…" : "Plan this match"}
        </ActionButton>
        {saveState === "failed" && <div className="nw-help">What you typed stays.</div>}
      </div>
    </div>
  );
}
